import React from 'react';
import { colors, spacing, shadows } from '../../../design/tokens';

interface PricingTierProps {
  name: string;
  price: string;
  whoIsThisFor: string;
  outcomes: string[];
  ctaText: string;
  ctaVariant?: 'primary' | 'secondary' | 'outline';
  powerfulLine: string;
  isPopular?: boolean;
  colorScheme?: 'green' | 'blue' | 'red';
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  price,
  whoIsThisFor,
  outcomes,
  ctaText,
  ctaVariant = 'primary',
  powerfulLine,
  isPopular = false,
  colorScheme = 'blue',
}) => {
  const getSchemeColor = () => {
    switch (colorScheme) {
      case 'green': return colors.healthy.main;
      case 'red': return colors.critical.main;
      default: return colors.brand.main;
    }
  };

  const schemeColor = getSchemeColor();

  return (
    <div style={{
      padding: spacing.xl,
      borderRadius: '12px',
      backgroundColor: colors.background.surface,
      boxShadow: isPopular ? `0 0 0 2px ${schemeColor}, ${shadows.xl}` : shadows.lg,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      height: '100%',
      border: `1px solid ${colors.neutral[200]}`,
    }}>
      {isPopular && (
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: schemeColor,
          color: 'white',
          padding: `${spacing.xs} ${spacing.md}`,
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 'bold',
          textTransform: 'uppercase',
        }}>
          Most Popular
        </div>
      )}
      
      <div style={{ marginBottom: spacing.md }}>
        <h3 style={{ margin: 0, fontSize: '24px', color: colors.neutral[900] }}>{name}</h3>
        <p style={{ color: colors.neutral[500], fontSize: '14px', marginTop: spacing.xs }}>{whoIsThisFor}</p>
      </div>

      <div style={{ marginBottom: spacing.lg }}>
        <span style={{ fontSize: '36px', fontWeight: 'bold', color: colors.neutral[900] }}>{price}</span>
        {price !== 'Custom' && <span style={{ color: colors.neutral[500] }}>/month</span>}
      </div>

      <div style={{ 
        fontStyle: 'italic', 
        color: schemeColor, 
        marginBottom: spacing.lg,
        fontWeight: 500 
      }}>
        “{powerfulLine}”
      </div>

      <ul style={{ 
        listStyle: 'none', 
        padding: 0, 
        margin: `0 0 ${spacing.xl} 0`, 
        flex: 1 
      }}>
        {outcomes.map((outcome, idx) => (
          <li key={idx} style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            marginBottom: spacing.sm,
            fontSize: '14px',
            color: colors.neutral[700]
          }}>
            <span style={{ color: schemeColor, marginRight: spacing.sm }}>✓</span>
            {outcome}
          </li>
        ))}
      </ul>

      <button style={{
        padding: `${spacing.md} ${spacing.lg}`,
        borderRadius: '8px',
        border: 'none',
        backgroundColor: ctaVariant === 'primary' ? schemeColor : 'transparent',
        color: ctaVariant === 'primary' ? 'white' : schemeColor,
        fontWeight: 'bold',
        cursor: 'pointer',
        border: ctaVariant === 'outline' ? `2px solid ${schemeColor}` : 'none',
        transition: 'all 0.2s',
      }}>
        {ctaText}
      </button>
    </div>
  );
};

export const PricingPage: React.FC = () => {
  return (
    <div style={{ 
      backgroundColor: colors.neutral[50], 
      minHeight: '100vh',
      padding: `${spacing.xxl} 0` 
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: `0 ${spacing.lg}`,
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '48px', color: colors.neutral[900], marginBottom: spacing.md }}>
          Pricing Plans
        </h1>
        <p style={{ fontSize: '18px', color: colors.neutral[600], maxWidth: '700px', margin: '0 auto' }}>
          Choose the right plan to accelerate your development workflow.
        </p>

        <div style={{ 
          margin: `${spacing.xl} 0`, 
          padding: spacing.lg, 
          backgroundColor: colors.brand.light, 
          borderRadius: '12px',
          display: 'inline-block'
        }}>
          <span style={{ fontSize: '20px', fontWeight: 'bold', color: colors.brand.dark }}>
            🔥 Reduce debugging time by up to 50–70% with automated fixing and validation
          </span>
        </div>

        {/* Individuals & Small Teams */}
        <div style={{ marginTop: spacing.xxl }}>
          <h2 style={{ textAlign: 'left', marginBottom: spacing.xl }}>For Individuals & Serious Developers</h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: spacing.xl 
          }}>
            <PricingTier 
              name="Starter"
              price="₹999"
              whoIsThisFor="Individuals trying the product"
              powerfulLine="Start fixing code faster"
              ctaText="Get Started"
              colorScheme="green"
              outcomes={[
                "Basic AI chat & fixes",
                "Limited executions/month",
                "No auto validation loop",
                "No advanced security"
              ]}
            />
            <PricingTier 
              name="Pro"
              price="₹4,999"
              whoIsThisFor="Serious developers"
              powerfulLine="Automate debugging and testing"
              ctaText="Upgrade to Pro"
              isPopular={true}
              colorScheme="blue"
              outcomes={[
                "Full execution (fix + build + test)",
                "Auto retry engine",
                "Basic security analysis",
                "Moderate usage"
              ]}
            />
            <PricingTier 
              name="Power User"
              price="₹9,999"
              whoIsThisFor="Power users / freelancers / small teams"
              powerfulLine="Ship stable code with confidence"
              ctaText="Unlock Full Power"
              colorScheme="red"
              outcomes={[
                "Priority execution",
                "Faster processing",
                "Advanced security (OWASP reports)",
                "Version upgrade support",
                "Higher usage"
              ]}
            />
          </div>
        </div>

        {/* Enterprise Sections */}
        <div style={{ marginTop: spacing.xxl * 2 }}>
          <h2 style={{ textAlign: 'left', marginBottom: spacing.xl }}>💼 Enterprise — Outcome Driven</h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: spacing.xl 
          }}>
            <PricingTier 
              name="Team"
              price="₹50,000"
              whoIsThisFor="5–10 developers"
              powerfulLine="Transform engineering productivity at scale"
              ctaText="Book Demo"
              colorScheme="green"
              outcomes={[
                "CI/CD integration",
                "Auto fix + validation",
                "Basic reporting"
              ]}
            />
            <PricingTier 
              name="Growth"
              price="₹2L–₹5L"
              whoIsThisFor="Expanding teams needing full automation"
              powerfulLine="Automate workflows and scale fast"
              ctaText="Schedule Demo"
              isPopular={true}
              colorScheme="blue"
              outcomes={[
                "Full automation workflows",
                "Version upgrade engine",
                "Security + compliance reports",
                "Team analytics dashboard"
              ]}
            />
            <PricingTier 
              name="Enterprise"
              price="₹5L–₹25L+"
              whoIsThisFor="Custom solutions for large teams"
              powerfulLine="End-to-end automation at scale"
              ctaText="Schedule Demo"
              colorScheme="red"
              outcomes={[
                "Migration projects",
                "Custom workflows",
                "Dedicated support",
                "SLA guarantees"
              ]}
            />
          </div>
        </div>

        <div style={{ marginTop: spacing.xxl, borderTop: `1px solid ${colors.neutral[200]}`, paddingTop: spacing.xl }}>
          <h3 style={{ color: colors.neutral[800] }}>Used across real-world projects</h3>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: spacing.xl, 
            marginTop: spacing.lg,
            color: colors.neutral[500],
            fontWeight: 500
          }}>
            <span>Auto-fix + test validation engine</span>
            <span>•</span>
            <span>OWASP-based security analysis</span>
          </div>
          <p style={{ marginTop: spacing.xl, color: colors.neutral[600], fontSize: '18px' }}>
            “Teams using Exibrain reduce debugging time by up to 50–70%.”
          </p>
        </div>
      </div>
    </div>
  );
};
